import {
  Capability,
  ROLES_MAP,
  getCanViewPage,
  hasSectionCapability,
} from "../../shared/capabilities";

import {
  activitySelectors,
  createAppSelector,
  createStringArraySelector,
  pageSelectors,
  selectCurrentPageId,
  selectCurrentSectionId,
  selectCurrentUserId,
} from "./base";
import { selectCurrentUserComputedRole } from "./permissions";
import { selectEnabledSectionUsers } from "./section-user";
import { getSortedPages, sortTopicActivities } from "./sorting";
import type { PageWithTopicIdType } from "./types";

import type { PageCategory, PageType } from "components/server-types";
import { DEFAULT_ORDER_FN } from "mds/hooks/use-ordered";
import type { RootState } from "store";
import { compactMap, filterMap } from "utils/collections";
import { isGroupPage, isUnassessablePage } from "utils/page-utils";
import {
  CONTENT_SIDEBAR_TAB_NAME,
  HOME_ROLE_TO_SECTION_ROLES,
  HomeRole,
  SectionPageCategoryToSidebarTab,
  pageUrl,
  sectionHomeUrl,
} from "utils/urls";

export const selectActivityByPageId = createAppSelector(
  [pageSelectors.selectById, activitySelectors.selectEntities],
  (page, activities) => (page?.activity_id && activities[page.activity_id]) || null,
);

export const selectTopicIdByPageId = createAppSelector(
  [selectActivityByPageId],
  (activity) => activity?.topic_id ?? null,
);

export const selectPagesByActivityId = createAppSelector(
  [
    pageSelectors.selectAll,
    (_state, activityId?: string | null) => activityId,
    selectCurrentUserComputedRole,
  ],
  (pages, activityId, computedRole) => {
    if (!activityId) return [];
    return pages.filter(
      (page) => page.activity_id === activityId && getCanViewPage(page, computedRole),
    );
  },
);

export const selectIsUnassessablePage = createAppSelector(
  [pageSelectors.selectById, selectActivityByPageId],
  (page, activity) => isUnassessablePage(page, activity?.category),
);

export const selectCurrentPage = createAppSelector(
  [selectCurrentPageId, pageSelectors.selectEntities],
  (pageId, pages) => (pageId && pageId in pages ? pages[pageId] : null),
);

export const selectCurrentTopicId = createAppSelector(
  [selectCurrentPage, activitySelectors.selectEntities],
  (page, activities) => {
    const activityId = page?.activity_id;
    if (!activityId) return null;
    return activities[activityId]?.topic_id ?? null;
  },
);

export const selectWorksheetIdByPage = (state: RootState, pageId: string | null) =>
  pageSelectors.selectById(state, pageId)?.worksheet_id ?? null;

export const selectPageIdByWorksheetId = createAppSelector(
  [pageSelectors.selectAll, (state, worksheetId: string) => worksheetId],
  (pages, worksheetId) => {
    return pages.find((page) => page.worksheet_id === worksheetId)?.id ?? null;
  },
);

export const selectPageCategoryByPageId = createAppSelector(
  [pageSelectors.selectById],
  (page) => page?.category ?? null,
);

export const selectPageSectionIdByPageId = createAppSelector(
  [pageSelectors.selectById],
  (page) => page?.section_id ?? null,
);

/**
 * Section ids for optional homeRole: role-scoped enrollments, or current section for ADMIN/unset.
 * Memoized so page churn does not rebuild the enrollment-derived id list.
 */
export const selectSectionIdsForOptionalHomeRole = createStringArraySelector(
  [
    selectEnabledSectionUsers,
    selectCurrentUserId,
    selectCurrentSectionId,
    (_state, homeRole?: HomeRole | null) => homeRole,
  ],
  (sectionUsers, userId, currentSectionId, homeRole): string[] => {
    // Admin / unset → current section only (ADMIN maps to null in HOME_ROLE_TO_SECTION_ROLES).
    const sectionRole = homeRole != null ? HOME_ROLE_TO_SECTION_ROLES[homeRole] : null;
    if (!sectionRole) {
      return currentSectionId ? [currentSectionId] : [];
    }
    return filterMap(
      sectionUsers,
      (sectionUser) => sectionUser.user_id === userId && sectionUser.role === sectionRole,
      (sectionUser) => sectionUser.section_id,
    );
  },
);

export const selectAuthorizedPages = createAppSelector(
  [
    pageSelectors.selectAll,
    selectCurrentUserComputedRole,
    selectSectionIdsForOptionalHomeRole,
    (_state, homeRole?: HomeRole | null) => homeRole,
  ],
  (pages, currentComputedRole, sectionIds, homeRole) => {
    const sectionRole = homeRole != null ? HOME_ROLE_TO_SECTION_ROLES[homeRole] : null;
    const sectionIdSet = new Set(sectionIds);
    const effectiveRole = sectionRole ? ROLES_MAP[sectionRole] : currentComputedRole;
    return pages.filter(
      (page) => sectionIdSet.has(page.section_id) && getCanViewPage(page, effectiveRole),
    );
  },
);

/** Authorized page ids (optional homeRole forwarded from selectAuthorizedPages). */
export const selectAuthorizedPageIds = createStringArraySelector(
  [selectAuthorizedPages],
  (pages): string[] => pages.map((page) => page.id),
);

/** Authorized group-page ids (optional homeRole forwarded from selectAuthorizedPages). */
export const selectAuthorizedGroupPageIds = createStringArraySelector(
  [selectAuthorizedPages],
  (pages): string[] => compactMap(pages, (page) => (isGroupPage(page) ? page.id : null)),
);

/** Authorized group pages with topic_id from the activity. */
export const selectAuthorizedGroupPagesWithTopicId = createAppSelector(
  [selectAuthorizedGroupPageIds, pageSelectors.selectEntities, activitySelectors.selectEntities],
  (pageIds, pageEntities, activityEntities): PageWithTopicIdType[] =>
    compactMap(pageIds, (pageId) => {
      const page = pageEntities[pageId];
      if (!page) {
        return null;
      }
      if (page.activity_id) {
        return {
          ...page,
          topic_id: activityEntities[page.activity_id]?.topic_id || undefined,
        } as PageWithTopicIdType;
      }
      return page as PageWithTopicIdType;
    }),
);

export const selectPagesForCurrentSection = createAppSelector(
  [(state, category?: PageCategory | null) => category, (state) => selectAuthorizedPages(state)],
  (category, pages): PageType[] => pages.filter((p) => !category || p.category === category),
);

export const selectPageIdsForCurrentSection = createAppSelector(
  [selectPagesForCurrentSection],
  (pages) => pages.map((p) => p.id),
);

export const selectHasManageableSectionContent = createAppSelector(
  [selectAuthorizedPages],
  (pages) => pages.some((page) => page.category !== "instructor_workspace"),
);

// When deleting the page the user is currently on, we need to redirect to another page.
// This selector provides the URL to redirect to.
// - If the page we are deleting is part of a topic (whether it has a due date or not), we redirect to the previous
//   page in the same topic. If the page was the first page of the topic, we redirect to the next one within the same
//   topic. We try to stay within the same activity/subcategory (prep work, additional resources, etc.) but if
//   there are no other pages in that category, we will redirect to the previous or next page within the same topic.
// - If the page is an assignment (but not part of a topic), course resource or private workspace page, we redirect to
//   the previous page within the same section. If the page was the first page of the section, we redirect to the next
//   one within the same section.
// - If there are no page left within that topic or section, we redirect to the section page.
export const selectRedirectUrlForPageDeletion = createAppSelector(
  [
    (state, targetPageId: string | undefined | null) => targetPageId,
    selectCurrentSectionId,
    selectCurrentUserComputedRole,
    selectCurrentPage,
    activitySelectors.selectAll,
    pageSelectors.filterForCurrentSection,
  ],
  (targetPageId, currentSectionId, computedRole, page, activities, pages) => {
    const canAuthorSection = hasSectionCapability(computedRole, Capability.CAN_AUTHOR_SECTION);
    if (!canAuthorSection || !currentSectionId || !page || page.id !== targetPageId) {
      return null;
    }
    const SECTION_OVERVIEW_URL = sectionHomeUrl(currentSectionId);
    let sortedPages: PageType[] = [];
    if (page.category === "activity_page") {
      const activity = activities.find((a) => a.id === page.activity_id);
      const topicId = activity?.topic_id;
      if (!topicId) {
        return SECTION_OVERVIEW_URL;
      }
      const sortedActivities = sortTopicActivities(topicId, activities, pages, computedRole);
      sortedPages = getSortedPages(sortedActivities);
    } else {
      sortedPages = pages.filter((p) => p.category === page.category).sort(DEFAULT_ORDER_FN);
    }
    const currentPageIndex = sortedPages.findIndex((p) => p.id === page.id);
    if (currentPageIndex === -1) {
      return SECTION_OVERVIEW_URL;
    }
    // If at all possible, we try to stay within the same activity/subcategory
    const previousPage = sortedPages[currentPageIndex - 1];
    const nextPage = sortedPages[currentPageIndex + 1];
    // sortedPages is either empty or only contains the current page.
    if (!previousPage && !nextPage) {
      return SECTION_OVERVIEW_URL;
    }
    // For the first page of sortedPages, we always redirect to the next page.
    if (!previousPage) {
      const tab = SectionPageCategoryToSidebarTab[nextPage.category] as CONTENT_SIDEBAR_TAB_NAME;
      return pageUrl(currentSectionId, nextPage.id, { tab });
    }
    // For the last page of sortedPages, we always redirect to the previous page.
    if (!nextPage) {
      const tab = SectionPageCategoryToSidebarTab[
        previousPage.category
      ] as CONTENT_SIDEBAR_TAB_NAME;
      return pageUrl(currentSectionId, previousPage.id, { tab });
    }
    // If the previous page is part of the same activity/subcategory, we redirect to it.
    if (previousPage.activity_id === page.activity_id) {
      const tab = SectionPageCategoryToSidebarTab[
        previousPage.category
      ] as CONTENT_SIDEBAR_TAB_NAME;
      return pageUrl(currentSectionId, previousPage.id, { tab });
    }
    // If the next page is part of the same activity/subcategory, we redirect to it.
    if (nextPage.activity_id === page.activity_id) {
      const tab = SectionPageCategoryToSidebarTab[nextPage.category] as CONTENT_SIDEBAR_TAB_NAME;
      return pageUrl(currentSectionId, nextPage.id, { tab });
    }
    // If we can't stay within the same activity/subcategory, we redirect to the previous page.
    const tab = SectionPageCategoryToSidebarTab[previousPage.category] as CONTENT_SIDEBAR_TAB_NAME;
    return pageUrl(currentSectionId, previousPage.id, { tab });
  },
);
